/**
 * Public FAQ Service
 *
 * Handles all public-facing FAQ operations (no authentication required)
 */

import api from './api';
import type {
  FaqCategoriesResponse,
  FaqQuestionResponse,
  FaqSearchResponse,
} from '../types/faq';

/**
 * Get all published FAQ categories with their published questions
 * @param category - Optional category slug to filter by (e.g., "pricing-plans")
 */
export const getPublicFaqs = async (category?: string): Promise<FaqCategoriesResponse> => {
  const response = await api.get<FaqCategoriesResponse>('/api/faq', {
    params: category ? { category } : undefined,
  });
  return response.data;
};

/**
 * Get a single published FAQ question by ID
 * This will increment the view count
 */
export const getPublicQuestion = async (id: number): Promise<FaqQuestionResponse> => {
  const response = await api.get<FaqQuestionResponse>(`/api/faq/${id}`);
  return response.data;
};

/**
 * Search published FAQ questions by query string
 */
export const searchPublicFaqs = async (query: string): Promise<FaqSearchResponse> => {
  const response = await api.get<FaqSearchResponse>('/api/faq/search', {
    params: { q: query },
  });
  return response.data;
};

/**
 * Get translated text based on current language.
 * Falls back to default text if translation not available.
 */
export const getTranslatedText = (
  defaultText: string,
  translations: { [key: string]: string } | undefined,
  lang: string
): string => {
  if (!translations || lang === 'en') {
    return defaultText;
  }

  return translations[lang] || defaultText;
};

/**
 * Export service object for convenience
 */
const faqService = {
  getPublicFaqs,
  getPublicQuestion,
  searchPublicFaqs,
  getTranslatedText,
};

export default faqService;
